import {
  CORP_HOMEPAGE_SUBTITLE,
  CORP_HOMEPAGE_TITLE,
  isCaponeTenant,
  isCorpTenant,
  useUserContext,
} from "@capone/common";
import {
  HomePageHotelSearch,
  FlightsSearchForm,
  HomePagePackageSearch,
  HomePageActivitiesSearch,
  HotelTrackingProvider,
  FlightTrackingProvider,
} from "@capone/portal";
import {
  HomePageHotelSearch as CorpHomePageHotelSearch,
  FlightsSearchForm as CorpFlightsSearchForm,
  FlightTrackingProvider as CorpFlightTrackingProvider,
  FreeDisruptionRebookingSearchBanner,
  OfferSearchBanner,
} from "@capone-corporate/portal";
import { Skeleton } from "@material-ui/lab";
import clsx from "clsx";
import {
  CorporateTypography,
  TravelerWorkflowDialog,
  useDeviceTypes,
  TravelAppFTUXModal,
} from "halifax";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import {
  CallState,
  CorpSessionInfo,
  CustomEvents,
  EditTravelerModalState,
  Funnel,
  IPerson,
  ModalNames,
  RewardsAccount,
  SavedTravelersProperties,
  SelfServeEvents,
  SessionInfo,
  Tenant,
  WalletDetailsResponse,
} from "redmond";
import { Typography } from "@material-ui/core";
import { trackEvent } from "../../api/v1/trackEvent";
import {
  deleteUserPassenger,
  fetchCorpUserPassengers,
  setSessionInfoInStore,
  updateUserPassenger,
} from "../../app/actions";
import { getUserPassengers } from "../../app/selectors";
import {
  ANNUAL_TRAVEL_CREDITS,
  AVAILABLE,
  CARS_LOYALTY_SECTION,
  CONTROL,
  getExperimentVariant,
  getExperimentVariantCustomVariants,
  HOTEL_CROSS_SELL_V3,
  HOTEL_CROSS_SELL_V3_VARIANTS,
  HOTELS_HOMEPAGE_CROSS_SELL_EXPERIMENT,
  RECENTLY_VIEWED_DESKTOP_P0_EXPERIMENT,
  RECENTLY_VIEWED_DESKTOP_P0_VARIANTS,
  TRAVEL_APP_GTM_EXPERIMENT,
  useExperiments,
  XMOD_V1_EXPERIMENT,
} from "../../context/experiments";
import { useSeenModalUpdater } from "../../hooks/useSeenModalUpdater";
import config from "../../utils/config";
import { CrossSellProps } from "../Body/cross-sell-helpers";
import { RecentlyViewedAndSearchedProps } from "../Body/recently-viewed-and-searched-helpers";
import { CorpHomePageBottomSection } from "../capone-corporate/CorpHomePageBottomSection/component";
import { CorpHomePageTopSection } from "../capone-corporate/CorpHomePageTopSection/component";
import { CarSearch } from "../CarSearch";
import { HomepageAnnualTravelCredit } from "../HomepageAnnualTravelCredit";
import { HomepageHotelCrossSell } from "../HomepageHotelCrossSell";
import { LandingPageType, MobileLandingPage } from "../MobileLandingPage";
import { RecentlyViewedAndSearched } from "../RecentlyViewedAndSearched";
import styles from "./styles.module.scss";
import {
  EARN_ENHANCEMENT_TITLE,
  getFallbackTitle,
  PARADISE_TITLE,
  STAYS_SUBTITLE,
  SUBTITLE,
} from "./textConstants";
import { getAdsCarouselByTenant } from "../AdsCarousel";

type HopePageType = {
  type: LandingPageType;
  rewardsAccounts: RewardsAccount[];
  selectedRewardsAccountId: string | null;
  fetchRewardsAccountsCallState: CallState;
  walletDetails: WalletDetailsResponse | null;
  crossSellProps: CrossSellProps | null;
  recentlyViewedAndSearchedProps: RecentlyViewedAndSearchedProps | null;
  recentlyViewedAndSearchedLoaded: boolean;
};
/**
 * Component to render all home page products
 * TODO: implement funnel handling (cars, flights, hotels, etc)
 */
export const HomePage = ({
  type,
  rewardsAccounts,
  selectedRewardsAccountId,
  fetchRewardsAccountsCallState,
  walletDetails,
  crossSellProps,
  recentlyViewedAndSearchedProps,
  recentlyViewedAndSearchedLoaded,
}: HopePageType) => {
  const { matchesMobile } = useDeviceTypes();
  const location = useLocation();

  const { sessionInfo } = useUserContext(config.TENANT);
  const [localSessionInfo, setLocalSessionInfo] = useState<
    CorpSessionInfo | SessionInfo | undefined
  >(sessionInfo);

  const expState = useExperiments();
  const [openTravelCreditFTUX, setOpenTravelCreditFTUX] =
    useState<boolean>(false);

  const dispatch = useDispatch();

  const travelers = useSelector(getUserPassengers);
  const isHotelCrossSellExperiment =
    getExperimentVariant(
      expState.experiments,
      HOTELS_HOMEPAGE_CROSS_SELL_EXPERIMENT
    ) === AVAILABLE;

  const isAnnualTravelCreditsExperiment =
    getExperimentVariant(expState.experiments, ANNUAL_TRAVEL_CREDITS) ===
    AVAILABLE;

  const isHotelCrossSellV3Enabled =
    getExperimentVariantCustomVariants(
      expState.experiments,
      HOTEL_CROSS_SELL_V3,
      HOTEL_CROSS_SELL_V3_VARIANTS
    ) !== CONTROL;

  const isRecentlyViewedDesktopP0Experiment =
    getExperimentVariantCustomVariants(
      expState.experiments,
      RECENTLY_VIEWED_DESKTOP_P0_EXPERIMENT,
      RECENTLY_VIEWED_DESKTOP_P0_VARIANTS
    ) !== CONTROL;

  const isXmodRedesign =
    getExperimentVariant(expState.experiments, XMOD_V1_EXPERIMENT) ===
    AVAILABLE;

  const isTravelAppGTMEnabled =
    getExperimentVariant(expState.experiments, TRAVEL_APP_GTM_EXPERIMENT) ===
    AVAILABLE;

  const showGroundLoyaltySection =
    isCorpTenant(config.TENANT) ||
    getExperimentVariant(expState.experiments, CARS_LOYALTY_SECTION) ===
      AVAILABLE;

  const funnel = useMemo(() => {
    switch (type) {
      case LandingPageType.FLIGHTS:
        return Funnel.Air;
      case LandingPageType.HOTELS:
        return Funnel.Lodging;
      case LandingPageType.STAYS:
        return Funnel.Homes;
      case LandingPageType.CARS:
        return Funnel.Ground;
      case LandingPageType.PREMIUM_STAYS:
        return Funnel.Homes;
      case LandingPageType.PACKAGES:
        return Funnel.Lodging;
      case LandingPageType.EXPERIENCES:
        return Funnel.Experiences;
      default:
        return undefined;
    }
  }, [type]);

  const isHomepageAdCarouselEnabled =
    funnel === Funnel.Lodging || funnel === Funnel.Homes;

  const showCrossSellV3UI =
    isHotelCrossSellV3Enabled && !!crossSellProps?.tysonsOffer;

  const largestValueAccount: RewardsAccount | undefined = React.useMemo(
    () => rewardsAccounts[0],
    [rewardsAccounts]
  );

  const hasEarnToDisplay = useMemo(() => {
    if (largestValueAccount == null) return false;
    const { earn } = largestValueAccount;
    switch (type) {
      case LandingPageType.FLIGHTS:
        return !!earn.flightsMultiplier;
      case LandingPageType.HOTELS:
      case LandingPageType.STAYS:
        return !!earn.hotelsMultiplier || !!earn.homesMultiplier;
      case LandingPageType.CARS:
        return !!earn.carsMultiplier;
      case LandingPageType.PREMIUM_STAYS:
        return !!earn.premierCollectionMultiplier;
      case LandingPageType.PACKAGES:
        return !!earn.hotelsMultiplier;
      case LandingPageType.EXPERIENCES:
        return !!earn.experiencesMultiplier;
      default:
        return false;
    }
  }, [largestValueAccount, type]);

  const getTitleToDisplay = (
    tenant: Tenant,
    primaryEarnRate: string | number,
    secondaryEarnRate?: string | number
  ) => {
    if (isCorpTenant(tenant)) {
      return (
        <CorporateTypography variant="h1">
          {CORP_HOMEPAGE_TITLE}
        </CorporateTypography>
      );
    }
    if (
      fetchRewardsAccountsCallState === CallState.Success &&
      largestValueAccount != null &&
      hasEarnToDisplay
    ) {
      return EARN_ENHANCEMENT_TITLE(
        primaryEarnRate,
        largestValueAccount.productDisplayName,
        largestValueAccount.rewardsBalance.currencyDescription ??
          largestValueAccount.rewardsBalance.currency,
        type,
        secondaryEarnRate
      );
    }
    if (
      fetchRewardsAccountsCallState === CallState.Success ||
      fetchRewardsAccountsCallState === CallState.Failed
    ) {
      if (
        largestValueAccount?.productDisplayName
          .toLowerCase()
          .includes("paradise")
      ) {
        return <span className="font-regular">{PARADISE_TITLE}</span>;
      }
      return <span className="font-regular">{getFallbackTitle(type)}</span>;
    }
    return <Skeleton className="title-loading" />;
  };

  const getSubtitleToDisplay = (tenant: Tenant) => {
    if (isCorpTenant(tenant)) {
      return (
        <CorporateTypography variant="h5" fontWeight="light">
          {CORP_HOMEPAGE_SUBTITLE}
        </CorporateTypography>
      );
    }
    if (
      fetchRewardsAccountsCallState === CallState.Success ||
      fetchRewardsAccountsCallState === CallState.Failed
    ) {
      if (type === LandingPageType.HOTELS || type === LandingPageType.STAYS) {
        return STAYS_SUBTITLE;
      }
      return SUBTITLE;
    }
    return <Skeleton className="subtitle-loading" />;
  };

  const search = useMemo(() => {
    switch (type) {
      case LandingPageType.HOTELS:
      case LandingPageType.STAYS:
        return (
          <div
            className={clsx(styles["search-container"], {
              [styles["capone-corporate"]]: isCorpTenant(config.TENANT),
              [styles.xmod]: isXmodRedesign,
            })}
          >
            <Typography variant="h2" className={styles["search-heading"]}>
              {largestValueAccount != null
                ? getTitleToDisplay(
                    config.TENANT,
                    largestValueAccount.earn.hotelsMultiplier,
                    largestValueAccount.earn.homesMultiplier
                  )
                : null}
            </Typography>
            <div className={styles["search-subtitle"]}>
              {getSubtitleToDisplay(config.TENANT)}
            </div>
            {isCorpTenant(config.TENANT) ? (
              <>
                <CorpHomePageHotelSearch className={styles.search} />
                {funnel != null && (
                  <OfferSearchBanner
                    currentFunnel={funnel}
                    className={styles["ftb-search-banner"]}
                  />
                )}
              </>
            ) : (
              <HotelTrackingProvider>
                <HomePageHotelSearch className={styles.search} />
              </HotelTrackingProvider>
            )}
          </div>
        );
      case LandingPageType.CARS: {
        return (
          <div
            className={clsx(styles["search-container"], {
              [styles["capone-corporate"]]: isCorpTenant(config.TENANT),
              [styles.xmod]: isXmodRedesign,
            })}
          >
            <Typography variant="h2" className={styles["search-heading"]}>
              {largestValueAccount != null
                ? getTitleToDisplay(
                    config.TENANT,
                    largestValueAccount.earn.carsMultiplier
                  )
                : null}
            </Typography>
            <div className={styles["search-subtitle"]}>
              {getSubtitleToDisplay(config.TENANT)}
            </div>
            <CarSearch className={styles.search} />
          </div>
        );
      }
      case LandingPageType.FLIGHTS: {
        return (
          <div
            className={clsx(styles["search-container"], styles.flights, {
              [styles["capone-corporate"]]: isCorpTenant(config.TENANT),
              [styles.xmod]: isXmodRedesign,
            })}
          >
            <Typography variant="h2" className={styles["search-heading"]}>
              {largestValueAccount != null
                ? getTitleToDisplay(
                    config.TENANT,
                    largestValueAccount.earn.flightsMultiplier
                  )
                : null}
            </Typography>
            <div className={styles["search-subtitle"]}>
              {getSubtitleToDisplay(config.TENANT)}
            </div>
            {isCorpTenant(config.TENANT) ? (
              <>
                <CorpFlightTrackingProvider>
                  <CorpFlightsSearchForm className={styles.search} />
                </CorpFlightTrackingProvider>
                <FreeDisruptionRebookingSearchBanner
                  className={styles["fdr-search-banner"]}
                />
              </>
            ) : (
              <FlightTrackingProvider>
                <FlightsSearchForm className={styles.search} />
              </FlightTrackingProvider>
            )}
          </div>
        );
      }
      case LandingPageType.PACKAGES: {
        return (
          <div
            className={clsx(styles["search-container"], {
              [styles["capone-corporate"]]: isCorpTenant(config.TENANT),
              [styles.xmod]: isXmodRedesign,
            })}
          >
            <Typography variant="h2" className={styles["search-heading"]}>
              {largestValueAccount != null
                ? getTitleToDisplay(
                    config.TENANT,
                    largestValueAccount.earn.hotelsMultiplier
                  )
                : null}
            </Typography>
            <div className={styles["search-subtitle"]}>
              {getSubtitleToDisplay(config.TENANT)}
            </div>
            <HomePagePackageSearch className={styles.search} />
          </div>
        );
      }
      case LandingPageType.EXPERIENCES: {
        return (
          <div
            className={clsx(styles["search-container"], {
              [styles["capone-corporate"]]: isCorpTenant(config.TENANT),
              [styles.xmod]: isXmodRedesign,
            })}
          >
            <Typography variant="h2" className={styles["search-heading"]}>
              {largestValueAccount != null
                ? getTitleToDisplay(
                    config.TENANT,
                    largestValueAccount.earn.experiencesMultiplier
                  )
                : null}
            </Typography>
            <div className={styles["search-subtitle"]}>
              {getSubtitleToDisplay(config.TENANT)}
            </div>
            <HomePageActivitiesSearch className={styles.search} />
          </div>
        );
      }
      case LandingPageType.PREMIUM_STAYS:
      default:
        console.error("Homepage funnel not implemented"); // eslint-disable-line no-console -- intentional error logging
        return null;
    }
  }, [type, largestValueAccount]);

  const [editTravelersOpen, setEditTravelersOpen] = useState(
    EditTravelerModalState.closed
  );

  const userRoles =
    sessionInfo && "corporateInfo" in sessionInfo
      ? (sessionInfo as CorpSessionInfo).corporateInfo.role
      : [];

  const defaultTravelerId =
    sessionInfo && "corporateInfo" in sessionInfo
      ? (sessionInfo as CorpSessionInfo).corporateInfo.defaultTravelerId
      : "";

  const hasSeenNfuInfoModal =
    (sessionInfo &&
      "corporateInfo" &&
      (sessionInfo as CorpSessionInfo).corporateInfo?.hasSeenModalMap?.[
        ModalNames.NFU_IN_PRODUCT_AWARENESS_PROFILES
      ]) ??
    false;

  const closeEditTravelersModal = useCallback(() => {
    setEditTravelersOpen(EditTravelerModalState.closed);
  }, []);

  const onEditTravelersStateChange = useCallback((ev: Event) => {
    const newEditModalState =
      (ev as CustomEvent).detail ?? EditTravelerModalState.closed;

    setEditTravelersOpen(newEditModalState);
  }, []);

  const handleAddPass = useCallback(() => {
    if (!isCorpTenant(config.TENANT)) {
      trackEvent({
        eventName: SelfServeEvents.ClickSavedTravelersAddNew,
        properties: {
          url: window.location.pathname,
        },
      });
    }
  }, []);

  useEffect(() => {
    if (isCorpTenant(config.TENANT) && rewardsAccounts.length > 0) {
      dispatch(fetchCorpUserPassengers());
    }
  }, [rewardsAccounts]);

  useEffect(() => {
    document.addEventListener(
      CustomEvents.editTravelersStateChange,
      onEditTravelersStateChange
    );

    return () => {
      document.removeEventListener(
        CustomEvents.editTravelersStateChange,
        onEditTravelersStateChange
      );
    };
  }, []);

  const isOpenTravelerModal = useMemo(
    () =>
      (editTravelersOpen === EditTravelerModalState.ground &&
        type === LandingPageType.CARS) ||
      (editTravelersOpen === EditTravelerModalState.hotel &&
        (type === LandingPageType.HOTELS || type === LandingPageType.STAYS)),
    [type, editTravelersOpen]
  );

  useEffect(() => {
    if (localSessionInfo) {
      dispatch(setSessionInfoInStore(localSessionInfo));
    }
  }, [localSessionInfo]);

  const handleSeenNfuInfoModal = useSeenModalUpdater(
    sessionInfo,
    setLocalSessionInfo
  );

  return matchesMobile ? (
    <MobileLandingPage
      type={type}
      rewardsAccounts={rewardsAccounts}
      selectedRewardsAccountId={selectedRewardsAccountId}
      fetchRewardsAccountsCallState={fetchRewardsAccountsCallState}
      recentlyViewedAndSearchedLoaded={recentlyViewedAndSearchedLoaded}
    />
  ) : (
    <>
      {search}
      <CorpHomePageTopSection
        sessionInfo={sessionInfo}
        trackEvent={trackEvent}
        funnel={funnel}
        accounts={rewardsAccounts}
        selectedAccountReferenceId={selectedRewardsAccountId}
      />
      {walletDetails && (
        <HomepageAnnualTravelCredit
          firstName={sessionInfo.userInfo.firstName}
          rewardsAccounts={rewardsAccounts}
          walletDetails={walletDetails}
          isAnnualTravelCreditsExperiment={isAnnualTravelCreditsExperiment}
          onFtuxModalOpenChange={setOpenTravelCreditFTUX}
        />
      )}
      {isTravelAppGTMEnabled && (
        <TravelAppFTUXModal
          isAnyOtherFtuxOpen={openTravelCreditFTUX}
          currentRoute={location.pathname}
          trackEvent={trackEvent}
        />
      )}
      {isHotelCrossSellExperiment && showCrossSellV3UI && crossSellProps ? (
        <HomepageHotelCrossSell
          searchLocationResult={crossSellProps.searchLocationResult}
          fromDate={crossSellProps.fromDate}
          untilDate={crossSellProps.untilDate}
          adultCount={crossSellProps.adultCount}
          childAges={crossSellProps.childAges}
          largestValueAccount={crossSellProps.largestValueAccount}
          latency={crossSellProps.latency}
          bannerText={crossSellProps.bannerText}
          tysonsOffer={crossSellProps.tysonsOffer}
        />
      ) : null}
      {isHomepageAdCarouselEnabled && getAdsCarouselByTenant(config.TENANT)}

      {(() => {
        switch (true) {
          case !!recentlyViewedAndSearchedProps &&
            isRecentlyViewedDesktopP0Experiment &&
            !!rewardsAccounts.length:
            return isCaponeTenant(config.TENANT) ? (
              <RecentlyViewedAndSearched
                recentSearchesAndViews={
                  recentlyViewedAndSearchedProps!.recentSearchesAndViews
                }
                largestValueAccount={
                  recentlyViewedAndSearchedProps!.largestValueAccount
                }
                latency={recentlyViewedAndSearchedProps!.latency}
                negativeMargin={recentlyViewedAndSearchedProps!.negativeMargin}
                loading={!recentlyViewedAndSearchedLoaded}
                showSkeleton={false}
              />
            ) : (
              <div>
                <RecentlyViewedAndSearched
                  recentSearchesAndViews={
                    recentlyViewedAndSearchedProps!.recentSearchesAndViews
                  }
                  largestValueAccount={
                    recentlyViewedAndSearchedProps!.largestValueAccount
                  }
                  latency={recentlyViewedAndSearchedProps!.latency}
                  negativeMargin={
                    recentlyViewedAndSearchedProps!.negativeMargin
                  }
                  loading={!recentlyViewedAndSearchedLoaded}
                  showSkeleton={isCorpTenant(config.TENANT)}
                />
              </div>
            );
          default:
            return null;
        }
      })()}

      {isHotelCrossSellExperiment && !showCrossSellV3UI && crossSellProps ? (
        <HomepageHotelCrossSell
          searchLocationResult={crossSellProps.searchLocationResult}
          fromDate={crossSellProps.fromDate}
          untilDate={crossSellProps.untilDate}
          adultCount={crossSellProps.adultCount}
          childAges={crossSellProps.childAges}
          largestValueAccount={crossSellProps.largestValueAccount}
          latency={crossSellProps.latency}
          bannerText={crossSellProps.bannerText}
          tysonsOffer={crossSellProps.tysonsOffer}
        />
      ) : null}
      <CorpHomePageBottomSection
        sessionInfo={sessionInfo}
        rewardsAccounts={rewardsAccounts}
      />
      <TravelerWorkflowDialog
        tenant={config.TENANT}
        showHotelLoyaltySection
        showGroundLoyaltySection={showGroundLoyaltySection}
        disableDriversLicenseValidation
        disallowSelect
        displayModalSubtitle
        onAddPass={handleAddPass}
        showAdditionalInfoSection
        showDriverLicenseSection
        showFrequentFlyerSection
        showGenderField
        showNationalityField
        buttonClassName="b2b"
        dialogProps={{
          className: "cars-edit-traveler",
          onBackdropClick: closeEditTravelersModal,
          onClose: closeEditTravelersModal,
          open: isOpenTravelerModal,
        }}
        errorMessage="Add or choose one traveler to continue."
        fullScreenWithBanner={matchesMobile}
        handleDeletePassenger={(personId: string) => {
          dispatch(deleteUserPassenger({ personId }));
        }}
        handleUpdatePassenger={(
          person: IPerson,
          hideProfileAction,
          entryPoint
        ) => {
          const frequentFlyerProgramsTotal = Reflect.ownKeys(
            person.frequentFlyer
          ).length;

          if (!isCorpTenant(config.TENANT)) {
            trackEvent({
              eventName: SelfServeEvents.ClickSavedTravelersSaveTraveler,
              properties: {
                url: window.location.pathname,
                frequent_flyer_program_added: frequentFlyerProgramsTotal > 0,
                num_frequent_flyer_programs: frequentFlyerProgramsTotal,
              } satisfies SavedTravelersProperties,
            });
          }
          dispatch(
            updateUserPassenger(
              { person, hideProfileAction },
              defaultTravelerId,
              entryPoint
            )
          );
        }}
        isMobile={matchesMobile}
        travelers={travelers}
        titles={{}}
        dialogContentProps={{}}
        userRoles={userRoles}
        trackEvent={trackEvent}
        defaultTravelerId={defaultTravelerId}
        hasSeenNfuInfo={hasSeenNfuInfoModal}
        handleSeenNfuInfoModal={handleSeenNfuInfoModal}
      />
    </>
  );
};
